import { triggerHaptic } from "@/lib/nativeHaptics";

interface Props {
  issueNumber: number;
  isSaved: boolean;
  onBack: () => void;
  onBookmarkTap: () => void;
  /** Hide the right-side bookmark/save control (e.g. when viewing a sample). */
  hideBookmark?: boolean;
}

/**
 * Results page top nav. Matches F24+F25 NativeHome height/safe-area.
 * Background flush with results page cream.
 */
export function ResultsTopNav({
  issueNumber,
  isSaved,
  onBack,
  onBookmarkTap,
  hideBookmark = false,
}: Props) {
  const padded = String(Math.max(1, issueNumber)).padStart(2, "0");
  // PQ-1: 44×52 column stack — 44 for icon row, 8 for label gap. Back button mirrors height for nav-row symmetry.
  const hitStyle: React.CSSProperties = {
    width: 44,
    height: 52,
    padding: 0,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  };
  const circleVisual: React.CSSProperties = {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.5)",
    border: "1px solid rgba(0,0,0,0.07)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4A4540",
    fontSize: 16,
  };
  const saveLabelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.04em",
    marginTop: 3,
    lineHeight: 1,
  };

  return (
    <div
      style={{
        background: "#E8E0D5",
        paddingTop: "var(--safe-top, env(safe-area-inset-top, 0px))",
        paddingLeft: 9,
        paddingRight: 9,
        paddingBottom: 0,
        boxSizing: "content-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 58,
        }}
      >
      <button
        type="button"
        aria-label="Back"
        onClick={() => {
          void triggerHaptic("tab-switch");
          onBack();
        }}
        style={hitStyle}
      >
        <span style={circleVisual}>
          <i className="ti ti-arrow-left" />
        </span>
      </button>

      <div style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            color: "#C84B2F",
            letterSpacing: "0.08em",
            fontWeight: 500,
          }}
        >
          LIKE2FIT · ISSUE NO. {padded}
        </div>
        <div
          style={{
            fontSize: 18,
            fontStyle: "italic",
            color: "#2C2820",
            fontFamily: "var(--font-serif, 'Instrument Serif', serif)",
            lineHeight: 1.2,
          }}
        >
          Your outfit
        </div>
      </div>

      {hideBookmark ? (
        <div style={{ width: 44, height: 52, flexShrink: 0 }} aria-hidden="true" />
      ) : (
        <button
          type="button"
          aria-label={isSaved ? "Outfit saved" : "Save outfit"}
          onClick={() => {
            if (!isSaved) void triggerHaptic("success");
            onBookmarkTap();
          }}
          style={hitStyle}
        >
          <span
            style={{
              ...circleVisual,
              color: isSaved ? "#C84B2F" : "#4A4540",
            }}
          >
            <i className={isSaved ? "ti ti-bookmark-filled" : "ti ti-bookmark"} />
          </span>
          <span
            style={{
              ...saveLabelStyle,
              color: isSaved ? "#C84B2F" : "#4A4540",
            }}
          >
            {isSaved ? "Saved" : "Save"}
          </span>
        </button>
      )}
      </div>
    </div>
  );
}
